/**
 * Support Store
 *
 * Pinia store for consultant feedback / support request state.
 * Manages the submit dialog, error log, nudge, and request list.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getApiErrorMessage, registerIdentityReset } from '@claimshield/shared'
import { supportApi, type SupportRequest, type SupportComment, type CreatePayload, type RequestType, type ClientOpenBug } from '../services/supportApi'
import type { ErrorEntry, PageContext } from '../lib/pageContext'

const MAX_ERRORS = 10

export const useSupportStore = defineStore('support', () => {
  const dialogOpen = ref(false)
  const prefill = ref<{
    request_type: RequestType
    description: string
    page_context: PageContext | null
    related_client_id?: string | null
    related_employee_id?: string | null
    link_label?: string | null
  }>({ request_type: 'bug', description: '', page_context: null })
  const errorLog = ref<ErrorEntry[]>([])
  const nudge = ref<ErrorEntry | null>(null)
  const requests = ref<SupportRequest[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const current = ref<SupportRequest | null>(null)
  const comments = ref<SupportComment[]>([])
  const clientBugs = ref<ClientOpenBug[]>([])

  // Non-admin staff read only their own requests (`comms.py`), so the list,
  // the open request and its comments belong to ONE signed-in user (RV-FE N3).
  // Every change of who is signed in drops them, and `generation` drops an
  // answer to a read the previous user started. The error log and nudge are
  // this user's too; the dialog closes.
  let generation = 0
  registerIdentityReset(() => {
    generation += 1
    dialogOpen.value = false
    errorLog.value = []
    nudge.value = null
    requests.value = []
    loading.value = false
    error.value = null
    current.value = null
    comments.value = []
    clientBugs.value = []
  })

  function pushError(e: ErrorEntry) {
    errorLog.value = [e, ...errorLog.value].slice(0, MAX_ERRORS)
  }
  function openRequest(p?: Partial<typeof prefill.value>) {
    prefill.value = { request_type: 'bug', description: '', page_context: null, related_client_id: null, related_employee_id: null, link_label: null, ...p }
    dialogOpen.value = true
  }
  function closeRequest() { dialogOpen.value = false }
  function showNudge(e: ErrorEntry) { nudge.value = e }
  function dismissNudge() { nudge.value = null }

  async function submit(p: CreatePayload): Promise<boolean> {
    error.value = null
    try { await supportApi.create(p); return true }
    catch (e) { error.value = getApiErrorMessage(e); return false }
  }
  async function fetchRequests(status?: string) {
    const gen = generation
    loading.value = true; error.value = null
    try {
      const items = (await supportApi.list(status ? { status } : undefined)).items
      if (gen === generation) requests.value = items
    } catch (e) {
      if (gen !== generation) return
      // A failed read shows the error, never a list it could not confirm.
      requests.value = []
      error.value = getApiErrorMessage(e)
    } finally {
      if (gen === generation) loading.value = false
    }
  }
  async function fetchOne(id: string) {
    const gen = generation
    const one = await supportApi.get(id)
    if (gen !== generation) return
    current.value = one
    const list = await supportApi.listComments(id)
    if (gen !== generation) return
    comments.value = list
  }
  async function addComment(id: string, body: string, isInternal = false) {
    const c = await supportApi.addComment(id, body, isInternal)
    comments.value = [...comments.value, c]
  }
  async function updateStatus(id: string, patch: Record<string, unknown>) {
    current.value = await supportApi.update(id, patch)
    await fetchRequests()
  }
  async function fetchClientBugs(clientId: string) {
    clientBugs.value = (await supportApi.listOpenByClient(clientId)).items
  }

  return {
    dialogOpen, prefill, errorLog, nudge, requests, loading, error, current, comments, clientBugs,
    pushError, openRequest, closeRequest, showNudge, dismissNudge,
    submit, fetchRequests, fetchOne, addComment, updateStatus, fetchClientBugs,
  }
})
